/**
 * `schemas/admin.ts` — `spec.md` §6.9, plus the two DB rows those endpoints return
 * verbatim (`index_state`, `index_jobs`) and the `audit_log` row every admin call writes.
 *
 * The honesty rule of §6.9 is encoded in the types, not just in prose:
 * `total` is `number | null` and `progress` has a floor of `-1`, because "`total` is
 * `null` and `progress` is `-1` when genuinely unknown" — a schema that made `total` a
 * plain number would force a route to fabricate one.
 */
import * as z from 'zod';
import { AckSchema, NonEmptyText, NullableText, NumericIdParams, PositiveIntParam, TlLongString, UnixSeconds, } from './common.js';
import { Bpm, Year } from './track.js';
/* ────────────────────────────────────────────────────────────────────────────
 * POST /api/admin/reindex
 * ──────────────────────────────────────────────────────────────────────────── */
/** `index_jobs.kind`, matching the DDL CHECK constraint exactly. */
export const REINDEX_KINDS = [
    'incremental',
    'backfill',
    'refresh_window',
    'peaks',
    'covers',
];
export const ReindexKind = z.enum(REINDEX_KINDS);
export const AdminReindexRequestSchema = z
    .strictObject({
    kind: ReindexKind,
    windowSize: z.int().min(1).max(100000).optional(),
})
    .refine((body) => body.kind === 'refresh_window' || body.windowSize === undefined, {
    error: 'windowSize only applies to kind=refresh_window',
    path: ['windowSize'],
});
/** `202 { jobId }` — the indexer polls every 5 s and claims the row. */
export const AdminReindexAcceptedSchema = z.strictObject({
    jobId: z.int().positive(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/admin/reindex/:jobId  ·  POST /api/admin/reindex/:jobId/cancel
 * ──────────────────────────────────────────────────────────────────────────── */
/** `index_jobs.state`, matching the DDL CHECK constraint exactly. */
export const INDEX_JOB_STATES = ['queued', 'running', 'done', 'failed', 'cancelled'];
export const IndexJobState = z.enum(INDEX_JOB_STATES);
/**
 * `progress` is `0..100` **or** `-1`. The `-1` is load-bearing: it is the only honest
 * answer while a backfill is running against an unknown message range, and a UI must
 * render it as an indeterminate bar rather than as 0%.
 */
export const JobProgress = z.int().min(-1).max(100);
export const AdminJobParamsSchema = z.object({ jobId: PositiveIntParam });
export const AdminJobStatusResponseSchema = z.strictObject({
    id: z.int().positive(),
    kind: ReindexKind,
    state: IndexJobState,
    processed: z.int().nonnegative(),
    /** `null` when genuinely unknown. NEVER fabricate a number (§5.2, §6.9). */
    total: z.int().nonnegative().nullable(),
    progress: JobProgress,
    error: NullableText,
    startedAt: UnixSeconds.nullable(),
    finishedAt: UnixSeconds.nullable(),
});
/** The full `index_jobs` row, including the columns the admin endpoint does not return. */
export const IndexJobRowSchema = AdminJobStatusResponseSchema.extend({
    paramsJson: z.string(),
    requestedBy: z.int().positive().nullable(),
    createdAt: UnixSeconds,
});
/** `POST /api/admin/reindex/:jobId/cancel` → `200`; §6.9 gives no body. */
export const AdminCancelJobResponseSchema = AckSchema;
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/admin/index-state
 * ──────────────────────────────────────────────────────────────────────────── */
/** `index_state.run_state` — the DDL comment's closed set. */
export const INDEX_RUN_STATES = ['idle', 'scanning', 'backfilling', 'paused', 'error'];
export const IndexRunState = z.enum(INDEX_RUN_STATES);
/**
 * The `index_state` row (`spec.md` §5.2), camel-cased. One row per channel; the whole
 * resume position is `maxMessageId` / `minMessageId`.
 */
export const IndexStateRowSchema = z.strictObject({
    /** BARE MTProto channel id — no `-100` prefix (§5.2). */
    channelId: z.int(),
    maxMessageId: z.int().nonnegative(),
    minMessageId: z.int().nonnegative(),
    backfillDone: z.boolean(),
    /** From the `limit: 0` probe. The completeness check's right-hand side. */
    serverAudioTotal: z.int().nonnegative(),
    lastRunAt: UnixSeconds.nullable(),
    lastSuccessAt: UnixSeconds.nullable(),
    lastError: NullableText,
    lastErrorAt: UnixSeconds.nullable(),
    /** Honoured across restarts, so a FLOOD_WAIT is not restarted away. */
    floodWaitUntil: UnixSeconds,
    tracksIndexed: z.int().nonnegative(),
    runState: IndexRunState,
});
/**
 * `drift = serverAudioTotal - rowCount`. Positive means the channel holds audio we have
 * not indexed; negative means we hold rows the channel no longer has (deletions not yet
 * reconciled). It is signed on purpose — an absolute value would hide the second case.
 */
export const AdminIndexStateResponseSchema = IndexStateRowSchema.extend({
    rowCount: z.int().nonnegative(),
    drift: z.int(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/admin/stats
 * ──────────────────────────────────────────────────────────────────────────── */
export const ADMIN_TOP_TRACKS_LIMIT = 20;
export const AdminTopTrackSchema = z.strictObject({
    id: z.int().positive(),
    title: NonEmptyText,
    performer: NullableText,
    playCount: z.int().nonnegative(),
});
export const AdminStatsResponseSchema = z.strictObject({
    users: z.int().nonnegative(),
    tracks: z.int().nonnegative(),
    plays: z.int().nonnegative(),
    favorites: z.int().nonnegative(),
    cacheBytes: z.int().nonnegative(),
    walBytes: z.int().nonnegative(),
    topTracks: z.array(AdminTopTrackSchema).max(ADMIN_TOP_TRACKS_LIMIT),
});
/* ────────────────────────────────────────────────────────────────────────────
 * POST /api/admin/tracks/:id — manual metadata override
 * ──────────────────────────────────────────────────────────────────────────── */
export const AdminTrackParamsSchema = NumericIdParams;
/**
 * Every field is optional, but at least one must be present. Nullable fields accept an
 * explicit `null` to CLEAR a value — which is distinct from omitting the key, and is
 * why `performer` is `NullableText.optional()` rather than `NonEmptyText.optional()`:
 * an operator must be able to say "this track genuinely has no performer".
 *
 * Applying this sets `meta_source='manual'` and `meta_confidence=100`, and the indexer
 * must never overwrite a manual row (§6.9).
 */
export const AdminTrackPatchRequestSchema = z
    .strictObject({
    title: NonEmptyText.max(500).optional(),
    performer: NullableText.optional(),
    album: NullableText.optional(),
    year: Year.nullable().optional(),
    bpm: Bpm.nullable().optional(),
    musicalKey: NullableText.optional(),
    /** Tag NAMES, resolved or created server-side. Replaces the manual tag set. */
    tags: z.array(NonEmptyText.max(120)).max(32).optional(),
})
    .refine((body) => Object.keys(body).length > 0, {
    error: 'a manual override must change at least one field',
});
export const AdminTrackPatchResponseSchema = AckSchema;
/** `POST /api/admin/tracks/:id/refresh-ref` → `200`; §6.9 gives no body. */
export const AdminRefreshRefResponseSchema = AckSchema;
/* ────────────────────────────────────────────────────────────────────────────
 * POST /api/admin/cache/evict
 * ──────────────────────────────────────────────────────────────────────────── */
/**
 * Exactly one of `docId` or `all` — `{}` would be an accidental no-op and
 * `{ docId, all: true }` an ambiguous request that could evict the whole cache when the
 * operator meant one file.
 */
export const AdminCacheEvictRequestSchema = z
    .strictObject({
    /** `cache_blobs.doc_id` = `tracks.tg_doc_id`, a TL long as a decimal string. */
    docId: TlLongString.optional(),
    all: z.boolean().optional(),
})
    .refine((body) => (body.docId !== undefined) !== (body.all === true), {
    error: 'pass exactly one of docId or all:true',
});
export const AdminCacheEvictResponseSchema = z.strictObject({
    freedBytes: z.int().nonnegative(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * POST /api/admin/users/:id/block
 * ──────────────────────────────────────────────────────────────────────────── */
export const AdminUserParamsSchema = NumericIdParams;
export const AdminUserBlockRequestSchema = z.strictObject({
    blocked: z.boolean(),
});
export const AdminUserBlockResponseSchema = AckSchema;
/* ────────────────────────────────────────────────────────────────────────────
 * audit_log — written by EVERY admin call (§6.9)
 * ──────────────────────────────────────────────────────────────────────────── */
export const AuditLogRowSchema = z.strictObject({
    id: z.int().positive(),
    at: UnixSeconds,
    userId: z.int().positive().nullable(),
    action: NonEmptyText,
    detail: z.string().nullable(),
});
/* ────────────────────────────────────────────────────────────────────────────
 * cache_blobs — the LRU bookkeeping row (§5.2), surfaced by the admin cache endpoints
 * ──────────────────────────────────────────────────────────────────────────── */
export const CacheBlobRowSchema = z.strictObject({
    docId: TlLongString,
    relPath: NonEmptyText,
    bytes: z.int().nonnegative(),
    complete: z.boolean(),
    lastUsed: UnixSeconds,
    hits: z.int().nonnegative(),
    createdAt: UnixSeconds,
});
//# sourceMappingURL=admin.js.map